// ══════════════════════════════════════════════════════════════
// api.js — Helper de fetch con JWT automático y cache TTL
// El token se setea desde App.js cuando hay login/logout
// ══════════════════════════════════════════════════════════════

const _raw = process.env.REACT_APP_API_URL || "http://localhost:8000";
const BASE_URL = _raw.endsWith("/api/v1") ? _raw : `${_raw}/api/v1`;

// URL base SIN /api/v1 — para componentes que construyen la ruta completa
export const API_BASE = _raw.endsWith("/api/v1") ? _raw.slice(0, -7) : _raw;

let _authToken = null;

// Cache en memoria para datos estáticos (servicios, profesionales, etc.)
// Se limpia automáticamente al cambiar el token (login/logout)
const _cache = new Map();
// Dedup de peticiones idénticas en vuelo (evita doble fetch en StrictMode)
const _inflight = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutos

// Clínica activa para admin multi-tenant. Persistida en localStorage para
// sobrevivir a reloads. Se envía al backend como header X-Clinica-Id en
// cada request; el backend solo la respeta si el rol es admin.
const _CLINICA_KEY = "cliniflow_admin_clinica_id";
let _clinicaSeleccionada = (() => {
  try { return localStorage.getItem(_CLINICA_KEY); } catch { return null; }
})();

export const setAuthToken = (token) => {
  _authToken = token;
  _cache.clear();
  _inflight.clear();
  // Logout: olvidar también la clínica seleccionada para no contaminar al
  // siguiente usuario que entre en este navegador.
  if (!token) {
    _clinicaSeleccionada = null;
    try { localStorage.removeItem(_CLINICA_KEY); } catch {}
  }
};

export const setClinicaSeleccionada = (id) => {
  _clinicaSeleccionada = id == null ? null : String(id);
  try {
    if (_clinicaSeleccionada) localStorage.setItem(_CLINICA_KEY, _clinicaSeleccionada);
    else localStorage.removeItem(_CLINICA_KEY);
  } catch {}
  _cache.clear();
  _inflight.clear();
};

export const getClinicaSeleccionada = () => _clinicaSeleccionada;

export const clearApiCache = () => { _cache.clear(); _inflight.clear(); };

export const getAuthToken = () => _authToken;

/**
 * Cabeceras autenticadas para `fetch` MANUAL (descargas de blob/PDF/ZIP, subidas
 * con FormData) que no pueden pasar por apiFetch() porque necesitan el Response
 * crudo. Inyecta el MISMO Authorization + X-Clinica-Id que apiFetch, evitando los
 * 401 (token mal leído) y 403 (admin sin X-Clinica-Id) de reconstruir headers a mano.
 * Pasa `extra` para añadir Content-Type u otras (omítelo en FormData).
 */
export const authHeaders = (extra = {}) => {
  const h = { ...extra };
  if (_authToken) h["Authorization"] = `Bearer ${_authToken}`;
  if (_clinicaSeleccionada) h["X-Clinica-Id"] = _clinicaSeleccionada;
  return h;
};

/**
 * Fetch con JWT automático.
 * Opciones extra (además de las de fetch estándar):
 *   cache: true  →  almacena el resultado 5 min y deduplica in-flight (solo GET)
 */
export async function apiFetch(path, options = {}) {
  const { cache: useCache = false, ...fetchOptions } = options;

  // Quitar trailing slash (redirect_slashes=False en FastAPI)
  path = path.replace(/\/(\?|$)/, (_, m) => m === "?" ? "?" : "");

  const isGet = !fetchOptions.method || fetchOptions.method === "GET";

  if (useCache && isGet) {
    const hit = _cache.get(path);
    if (hit && Date.now() < hit.expires) return hit.data;
    const pending = _inflight.get(path);
    if (pending) return pending;
  }

  const exec = async () => {
    const isFormData = fetchOptions.body instanceof FormData;
    const headers = {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...(fetchOptions.headers || {}),
    };
    if (_authToken) headers["Authorization"] = `Bearer ${_authToken}`;
    if (_clinicaSeleccionada) headers["X-Clinica-Id"] = _clinicaSeleccionada;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    let res;
    try {
      res = await fetch(`${BASE_URL}${path}`, { ...fetchOptions, headers, signal: controller.signal });
    } catch (err) {
      if (err.name === "AbortError") throw new Error("Tiempo de espera agotado. Verifica tu conexión.");
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }

    if (res.status === 401) {
      setAuthToken(null);
      window.location.href = "/";
      throw new Error("Sesión expirada");
    }

    if (!res.ok) {
      let body = null;
      try { body = await res.json(); } catch {}
      const rawDetail = body?.detail;
      const detailMsg = res.status >= 500
        ? "Error del servidor. Inténtalo de nuevo."
        : (typeof rawDetail === "string" ? rawDetail
            : Array.isArray(rawDetail)
              ? (rawDetail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(". ") || `Error ${res.status}`)
              : (rawDetail?.message || `Error ${res.status}`));
      const err = new Error(detailMsg);
      err.status = res.status;
      err.detail = rawDetail; // objeto completo si el backend devolvió detail estructurado
      throw err;
    }

    if (res.status === 204) return null;

    const data = await res.json();
    if (useCache && isGet && data !== null) {
      _cache.set(path, { data, expires: Date.now() + CACHE_TTL_MS });
    }
    return data;
  };

  if (useCache && isGet) {
    const wrapper = exec().finally(() => _inflight.delete(path));
    _inflight.set(path, wrapper);
    return wrapper;
  }
  return exec();
}
